<template>
  <div
    :class="['flex w-full shrink-0 flex-col bg-white p-4', !isDesktop && 'shadow-sm']"
    data-testid="pinning-header"
  >
    <div class="flex items-center justify-between">
      <h2 class="m-0 text-lg font-semibold text-gray-800">Get Free AI Edits</h2>
      <div class="flex items-center gap-1">
        <UseSvg icon="info-circle-bold" class="h-4 w-4 text-gray-500" />
        <button
          ref="howItWorksButtonRef"
          type="button"
          class="cursor-pointer border-0 bg-transparent p-0 text-sm !text-blue-700 underline"
          data-testid="how-it-works-button"
          @click="openHowItWorksModal"
          @mouseenter="isDesktop && showDesktopPopover()"
          @mouseleave="isDesktop && hideDesktopPopover()"
        >
          How it works
        </button>
      </div>
    </div>
    <!-- Desktop: hover popover -->
    <teleport to="body">
      <div
        v-if="isDesktop && isDesktopPopoverVisible"
        ref="desktopPopoverEl"
        class="fixed z-[2147483001] max-h-[90vh] w-[580px] overflow-y-auto rounded-lg border border-gray-200 bg-white p-4 text-sm shadow-lg"
        :style="desktopPopoverStyle"
        @mouseenter="showDesktopPopover()"
        @mouseleave="hideDesktopPopover()"
      >
        <ArtistEditsInformation />
      </div>
    </teleport>
    <!-- Mobile: fixed overlay -->
    <teleport to="body">
      <div
        v-if="!isDesktop && isHowItWorksOpen"
        class="fixed inset-0 z-[2147483001] flex items-center justify-center px-4"
      >
        <div class="absolute inset-0 bg-black/60" @click="closeHowItWorks" />
        <div
          class="relative max-h-[95dvh] w-full overflow-y-auto rounded-lg bg-white px-2.5 py-4 pb-[max(1rem,env(safe-area-inset-bottom))]"
        >
          <ArtistEditsInformation is-mobile @close="closeHowItWorks" />
        </div>
      </div>
    </teleport>
  </div>
</template>

<script setup>
import { nextTick, onUnmounted, ref } from "vue";

import UseSvg from "common/vue/components/UseSvg.vue";

import ArtistEditsInformation from "./ArtistEditsInformation.vue";

defineOptions({ name: "PinningHeader" });

const props = defineProps({
  isDesktop: { type: Boolean, default: false }
});

const howItWorksButtonRef = ref(null);
const isHowItWorksOpen = ref(false);
const isDesktopPopoverVisible = ref(false);
const desktopPopoverStyle = ref({});
const desktopPopoverEl = ref(null);
let popoverHideTimeout = null;

onUnmounted(() => clearTimeout(popoverHideTimeout));

function showDesktopPopover() {
  clearTimeout(popoverHideTimeout);
  const $button = howItWorksButtonRef.value;
  if ($button) {
    const rect = $button.getBoundingClientRect();
    const popoverWidth = 580;
    const gap = 8;
    const margin = 8;

    let left = rect.left - popoverWidth - gap;
    if (left < margin) {
      left = rect.right + gap;
    }
    left = Math.max(margin, Math.min(left, window.innerWidth - popoverWidth - margin));

    desktopPopoverStyle.value = { left: `${left}px`, top: "0px", visibility: "hidden" };
    isDesktopPopoverVisible.value = true;

    nextTick(() => {
      if (!isDesktopPopoverVisible.value) return;
      const el = desktopPopoverEl.value;
      if (el) {
        const popoverHeight = el.offsetHeight;
        const buttonCenterY = rect.top + rect.height / 2;
        const idealTop = buttonCenterY - popoverHeight / 2;
        const clampedTop = Math.max(
          margin,
          Math.min(idealTop, window.innerHeight - popoverHeight - margin)
        );

        desktopPopoverStyle.value = { left: `${left}px`, top: `${clampedTop}px` };
      }
    });
    return;
  }
  isDesktopPopoverVisible.value = true;
}

function hideDesktopPopover() {
  popoverHideTimeout = setTimeout(() => {
    isDesktopPopoverVisible.value = false;
  }, 150);
}

function openHowItWorksModal() {
  if (props.isDesktop) {
    showDesktopPopover();
  } else {
    isHowItWorksOpen.value = true;
  }
}

function closeHowItWorks() {
  isHowItWorksOpen.value = false;
}
</script>
